import { createContext, Dispatch, SetStateAction, useContext } from 'react';
import { TicketDefinition } from '../blocks/ticket-block/contracts';
import { EventCapacityData, ReservationModel, ReservationUpdate } from './contracts';

export type FormEventAPIHelpers = {
	getTicketDetails: (input: {
		eventId: string;
		allowedTickets: string[];
		token?: string;
	}) => Promise<{ status: 'success' | 'error'; tickets?: TicketDefinition[]; availableQuotaRemaining?: number; message?: string }>;
	createTicketReservation: (input: {
		formRequestId: string;
		eventId: string;
		ticketId?: string;
		count?: number;
		reservationId?: string;
		enforceCount?: boolean;
		token?: string;
	}) => Promise<{ reservation?: ReservationModel | null; isCapped?: boolean | null } | undefined>;
	expireTicketReservation: (input: { eventId: string; reservationId: string; token?: string }) => Promise<ReservationModel | undefined>;
	findReservationById: (input: { reservationId?: string; token?: string }) => Promise<{
		success: boolean;
		reservation?: ReservationModel;
		message?: string;
	}>;
	validateRsvpResponse: (input: { reservationId: string; token?: string }) => Promise<boolean>;
	updateReservationCount: (input: {
		formRequestId: string;
		reservationId: string;
		operation: ReservationUpdate;
		count: number;
		token?: string;
	}) => Promise<{ success: boolean; message?: string; reservationCount?: number }>;
	createReservation: (input: { eventId: string; count?: number; token?: string }) => Promise<EventCapacityData>;
	extendReservation: (input: {
		formRequestId: string;
		reservationId: string;
		messageKey: string;
		safeExpiry?: boolean;
		token?: string;
	}) => Promise<{
		success: boolean;
		reservation?: ReservationModel;
		message?: string;
	}>;
};

/**These are used in the form blocks and set the state of the form event context */
type FormEventContextApiHelpers = {
	updateReservationCount: (input: { operation: ReservationUpdate; count: number }) => Promise<boolean>;
	createReservation: (count?: number) => Promise<void>;
	extendReservation: (input: { messageKey: string; safeExpiry?: boolean }) => Promise<{
		success: boolean;
		reservation?: ReservationModel;
		message?: string;
	}>;
	validateRsvpResponse: () => Promise<boolean>;
};
type FormEventContextValue = Omit<
	FormEventAPIHelpers,
	'createReservation' | 'updateReservationCount' | 'extendReservation' | 'validateRsvpResponse'
> &
	FormEventContextApiHelpers & {
		event?: EventCapacityData;
		waitlistSubmitted: boolean;
		setEventCapacityData: Dispatch<SetStateAction<EventCapacityData | undefined>>;
		setWaitlistSubmitted: (waitlistSubmitted: boolean) => void;
	};

export const FormEventContext = createContext<FormEventContextValue | null>(null);

export const useFormEventContext = () => {
	const context = useContext(FormEventContext);
	if (!context) {
		throw new Error('useFormEventContext must be used within a FormEventContextProvider');
	}
	return context;
};

FormEventContext.displayName = 'FormEventContext';
